// "Come back where you started" for configure-elsewhere detours.
//
// Several flows send an admin away mid-task: an agent version needs an LLM
// model, so the card links to /llm-models; registering a knowledge base needs
// an embedding model, so that dialog links to /embedding-models. Before this,
// finishing the detour left you on the settings page with no way back except
// the browser button, having lost the agent you were configuring.
//
// A `?return_to=` parameter carries the origin, and the destination page sends
// you back once the thing you went for exists.

import { useSearchParams } from "@/lib/navigation";

export const RETURN_TO_PARAM = "return_to";

/**
 * Append `?return_to=<path>` to an internal link.
 *
 * `path` should be an app path (`/agents/olive/versions/1.0.0`). Anything else
 * is dropped rather than encoded — see {@link useReturnTo}.
 */
export function withReturnTo(href: string, returnTo: string): string {
  if (!isInternalPath(returnTo)) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`;
}

/**
 * A path is safe to navigate back to only if it stays inside this app.
 *
 * The value arrives from the URL, so it is attacker-controllable: a link to
 * `/llm-models?return_to=https://evil.example` would otherwise turn a trusted
 * admin page into an open redirect. Requiring a single leading slash rejects
 * absolute URLs and protocol-relative `//host` forms; a backslash or control
 * character is refused because browsers read `/\host` as `//host`; and the
 * value must still resolve to this origin once the browser parses it.
 */
export function isInternalPath(value: string | null | undefined): value is string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(value)) return false;
  const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  try {
    return new URL(value, origin).origin === origin;
  } catch {
    return false;
  }
}

/** The validated `return_to` for the current page, or null when absent/unsafe. */
export function useReturnTo(): string | null {
  const raw = useSearchParams().get(RETURN_TO_PARAM);
  return isInternalPath(raw) ? raw : null;
}

/** `decodeURIComponent` that keeps a malformed escape as written instead of throwing. */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** A short human label for a return path, e.g. "olive 1.0.0" or "agents". */
export function describeReturnTo(path: string): string {
  const parts = path.split("?")[0]!.split("/").filter(Boolean);
  if (parts[0] === "agents" && parts[1]) {
    const version = parts[2] === "versions" && parts[3] ? ` ${safeDecode(parts[3])}` : "";
    return `${safeDecode(parts[1])}${version}`;
  }
  return parts.length ? safeDecode(parts[parts.length - 1]!) : "where you were";
}
